import {
  type MonitoringRouteSummary,
  type MonitorRouteRow,
  ROUTE_AUTH_MODES,
  ROUTE_TYPE_BUCKETS,
} from "../../../shared/monitoringRouteAuth";
import {
  isRouteAccessDisabled,
  type RouteAccessPolicy,
} from "../../lib/routeAccessChanges";
import { ROUTE_AUTH_COLORS, routeAuthLabel } from "../../lib/routeAuthDisplay";
import {
  routeTypeBucketLabel,
  routeTypeLabel,
} from "../../lib/routeTypeDisplay";
import { META_SEP } from "../MetaSeparator";
import type { StatusSegment } from "../StatusBreakdownChart";

export function routeLabel(
  route: MonitorRouteRow,
  forceDisabled: boolean,
  policy?: RouteAccessPolicy
): string {
  const suffix = route.routeType
    ? `${META_SEP}${routeTypeLabel(route.routeType)}`
    : "";
  const disabled = isRouteAccessDisabled(route, forceDisabled, policy)
    ? `${META_SEP}disabled`
    : "";
  return `${route.route} (${routeAuthLabel(route.routeAuth)}${disabled}${suffix})`;
}

export function buildRouteAuthSegments(
  summary: MonitoringRouteSummary,
  forceDisabled: boolean,
  policy?: RouteAccessPolicy
): StatusSegment[] {
  // Every auth mode is an axis so the radar keeps a stable shape.
  return ROUTE_AUTH_MODES.map((mode) => {
    const { workflowCount, routeTypeCounts } = summary.byAuth[mode];
    const buckets = ROUTE_TYPE_BUCKETS.filter(
      (bucket) => routeTypeCounts[bucket] > 0
    );
    const disabledBuckets = buckets.filter((bucket) =>
      isRouteAccessDisabled(
        { routeAuth: mode, routeType: bucket },
        forceDisabled,
        policy
      )
    );
    const allDisabled =
      disabledBuckets.length > 0 && disabledBuckets.length === buckets.length;
    const status = allDisabled
      ? " (disabled)"
      : disabledBuckets.length > 0
        ? " (some disabled)"
        : "";
    return {
      key: mode,
      count: workflowCount,
      color: ROUTE_AUTH_COLORS[mode],
      label: `${routeAuthLabel(mode)}${status}`,
      note:
        mode === "public" && !allDisabled
          ? "Public links can be triggered by anyone with the URL — there’s no authentication. Treat them as exposed to the open internet and review them carefully."
          : status
            ? "Disabled routes are blocked by security settings. They remain listed so you can review their configured access."
            : undefined,
      details: buckets.map((bucket) => ({
        label: `${routeTypeBucketLabel(bucket)}${disabledBuckets.includes(bucket) ? " (disabled)" : ""}`,
        value: String(routeTypeCounts[bucket]),
      })),
    };
  });
}
